import { Constants } from 'app/shared/shared-constants';
import { NamedArray } from 'shell-models-lib';
import { DataFlowColumn } from '../../dataset/dataflowcolumn';
import { BaseSchemaGeneratorTransformation } from '../../dataflow/base-schema-generator-transformation';
import { Metamodel } from 'app/model/base/model';
import { Owned } from '../../base/named';
import { DecimalDataFlowType, StringDataFlowType, IDataFlowDataType, ArrayDataFlowType, MapDataFlowType } from '../../dataflow/dataflow-datatype';
import { Transformation } from '../../dataflow/transformation';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';
import { ComplexDataFlowType, ComplexField } from 'app/model/dataflow/dataflow-complex-datatype';
import { DataFlowExpressionUtils } from './expression.util';
import { DataFlowMapColumn } from 'app/model/dataflow/dataflow-map.column';
import { DataFlowExpressionBasedMapColumn } from 'app/model/dataflow/dataflow-expression-based-map.column';
import { dataFlowPatternParameter } from 'app/model/dataflow/dataflow-expression-utilities';
import { DataFlowUtils } from 'app/authoring/component/transformations/dataFlow-utils';
import { BaseColumnMappingTransformation } from 'app/model/dataflow/base-column-mapping-transformation';
import { Antlr4Utils } from './antlr4-utils';
import { IKeyColumnsEntity } from 'app/model/dataflow/dataflow-sink';
import { ExpressionColumnPatternPlainObject } from 'worker/app-workers/shared/plainObjects/expressionColumn.plainObject';
import { BaseSchemaModifierTransformation } from 'app/model/dataflow/base-schema-modifier-transformation';
import { IMapColumn } from 'app/model/dataflow/IMapColumn';

export interface IFieldInformation {
    fieldName: string;
    fieldType: IDataFlowDataType;
}

export class DataFlowSchemaAdapterUtils {
    public static getColumnsDsl(model: BaseColumnMappingTransformation): string {
        return this.convertSchemaToColumns(model.schema);
    }

    public static convertSchemaToColumns(schema: IMapColumn[]): string {
        if (schema && schema.length > 0) {
            const mappingStrings = schema.filter((mapping) => mapping.writeColumn).map((mapping) => {
                if (mapping instanceof DataFlowMapColumn) {
                    if (mapping.outputAlias === mapping.columnRef.name && !mapping.columnRef.complexFieldPath) {
                        return `${mapping.columnRef.serializableName}`;
                    }
                    const outputAliasSerializable = DataFlowUtils.needToEscape(mapping.outputAlias) ? `{${mapping.outputAlias}}` : mapping.outputAlias;
                    return `${outputAliasSerializable} = ${mapping.columnRef.serializableName}`;
                } else if (mapping instanceof DataFlowExpressionBasedMapColumn) {
                    const columnMode = mapping.deepHierarchy ? 'every' : 'each';
                    const assignment = mapping.dataFlowExpression.expression === dataFlowPatternParameter
                        ? ''
                        : `${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(mapping.dataFlowExpression.expression)} = ${dataFlowPatternParameter}`;

                    let hierarchyLevelExpression: string = mapping.hierarchyLevelExpression.expression;
                    if (mapping.hierarchyLevelExpression.templateExpression) {
                        hierarchyLevelExpression = DataFlowExpressionUtils.wrapExpressionConfigValue(mapping.hierarchyLevelExpression);
                    }

                    if (mapping.regexString) {
                        return `${columnMode}(` + (!!hierarchyLevelExpression ? `${hierarchyLevelExpression},` : '') +
                            `patternMatch(\`${DataFlowExpressionUtils.escapeSingleQuoteInDslString(mapping.regexString)}\``
                            + `${mapping.templateCondition.expression ? `, ${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(mapping.templateCondition.expression)}` : ''})`
                            + `${assignment ? `,\n\t\t\t${assignment}` : ''})`;
                    } else {
                        return `${columnMode}(` + (!!hierarchyLevelExpression ? `${hierarchyLevelExpression},` : '') +
                            `match(${DataFlowExpressionUtils.wrapExpressionWithEmptyValue(mapping.templateCondition.expression)})${assignment ? `,\n\t\t\t${assignment}` : ''})`;
                    }
                }

                return '';
            }).filter((mappingString) => mappingString);

            return `mapColumn(\n\t\t${mappingStrings.join(',\n\t\t')}\n\t)`;
        }
        return '';
    }

    public static addColumnPatternToTransform(transform: BaseSchemaModifierTransformation, columnObject: ExpressionColumnPatternPlainObject): void {
        const newColumnPattern = transform.addColumnPattern();
        newColumnPattern.templateCondition.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(columnObject.condition);
        if (columnObject.unEscapedRegexString) {
            newColumnPattern.regexString = DataFlowExpressionUtils.unescapeSingleQuoteInDslString(columnObject.unEscapedRegexString).slice(1, -1);
        }
        if (columnObject.hierarchyLevelExpression) {
            newColumnPattern.hierarchyLevelExpression.expression =
                DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(columnObject.hierarchyLevelExpression);
        }
        if (columnObject.typeExpression) {
            newColumnPattern.fixedReturnTypeExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(columnObject.typeExpression);
        }
        columnObject.columns.forEach((childColumn) => {
            const newColumn = transform.addColumn(newColumnPattern);
            newColumn.columnNameExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(childColumn.name);
            newColumn.dataFlowExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithEmptyValue(childColumn.expression);
            newColumn.column.name = newColumn.columnNameExpression.expression;
        });
    }

    public static getSchemaFromString(schemaString: string, transformation?: Transformation): NamedArray<DataFlowColumn> {
        // special handle for null[], replace it with string[]
        schemaString = schemaString.replace('null[]', 'string[]');
        // special handle for empty object, replace it with string
        schemaString = schemaString.replace('as ()', 'as string');

        const tokenStream = Antlr4Utils.getTokenStream(schemaString);
        const parser = Antlr4Utils.getParser(tokenStream);
        const outputDefinitionContext = parser.outputDefinition();
        return DataFlowSchemaAdapterUtils.getSchemaFromOutputDefinition(outputDefinitionContext, transformation);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static getSchemaFromOutputDefinition(outputDefinitionContext: any, transformation?: Transformation): NamedArray<DataFlowColumn> {
        const schema = NamedArray.create<DataFlowColumn>(transformation);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        outputDefinitionContext.columnDefinition().forEach((columnDefinition: any) => {
            const sourceColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
            const fieldInformation: IFieldInformation = DataFlowSchemaAdapterUtils.getFieldInformation(columnDefinition);
            sourceColumn.name = fieldInformation.fieldName;
            sourceColumn.dataFlowColumnType = DataFlowTypesUtils.cloneType(fieldInformation.fieldType);
            sourceColumn.dataFlowColumnType.format = fieldInformation.fieldType.format;
            schema.push(sourceColumn);
        });
        return schema;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static getColumnName(columnIdentifier: any): string {
        let columnName = '';
        if (columnIdentifier.basicName()) {
            columnName = columnIdentifier.getText();
        } else if (columnIdentifier.anyName()) {
            const anyIdentifier = columnIdentifier.anyName().ANY_IDENTIFIER();
            if (anyIdentifier) {
                const identifierText = anyIdentifier.getText();
                columnName = DataFlowSchemaAdapterUtils.removeWrappingCharacters(identifierText);
            } else {
                columnName = columnIdentifier.anyName().keyWord().getText();
            }
        }
        return columnName;
    }

    public static getOutputDefinitionDsl(model: BaseSchemaGeneratorTransformation): string {
        return DataFlowSchemaAdapterUtils.getOutputDefinitionDslForColumns(model.columns);
    }

    public static getOutputDefinitionDslForColumns(columns: DataFlowColumn[]): string {
        return DataFlowSchemaAdapterUtils.getDefinitionDslForColumns(columns, 'output');
    }

    public static getInputDefinitionDslForColumns(columns: DataFlowColumn[]): string {
        return DataFlowSchemaAdapterUtils.getDefinitionDslForColumns(columns, 'input');
    }

    public static getDefinitionDslForColumns(columns: DataFlowColumn[], actionName: string): string {
        if (columns && columns.length > 0) {
            const columnStrings = columns.filter((column) => column.dataFlowColumnType).map((column) => {
                const columnType = column.dataFlowColumnType || new StringDataFlowType();
                const columnName = DataFlowExpressionUtils.escapeAllNewlineInDslString(column.serializableColumnName);
                return `${columnName} as ${columnType.typeInDsl}`;
            });

            return columnStrings ? `${actionName}(\n\t\t${columnStrings.join(',\n\t\t')}\n\t)` : '';
        }
        return undefined;
    }

    public static removeWrappingCharacters(columnName: string): string {
        return columnName.substring(1, columnName.length - 1);
    }

    public static extractColumnName(qualifiedColumnName: string): string {
        const nameSplits = qualifiedColumnName.split(/@\s*(?![^{}]*\})/);
        const name = nameSplits[nameSplits.length - 1];
        return name.match(Constants.dataFlowEscapedNameValidation) ? DataFlowSchemaAdapterUtils.removeWrappingCharacters(name) : name;
    }

    public static getKeyColumnsProps(model: IKeyColumnsEntity): string {
        const keyColumns = model.keyColumns.filter((column) => column.expression);
        if (!model.isListOfKeyColumns && !!keyColumns[0]) {
            return `keys:${DataFlowExpressionUtils.wrapExpressionConfigValue(keyColumns[0], ArrayDataFlowType.TYPE)}`;
        } else {
            const columns = keyColumns.map((col) => DataFlowExpressionUtils.wrapExpressionConfigValue(col, StringDataFlowType.TYPE, true)).join(',');
            if (!!columns) {
                return `keys:[${columns}]`;
            }
        }
        return '';
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public static getFieldInformation(columnDefinition: any): IFieldInformation {
        const fieldInformation: IFieldInformation = {
            fieldName: DataFlowSchemaAdapterUtils.getColumnName(columnDefinition.columnIdentifier()),
            fieldType: DataFlowTypesUtils.createType(StringDataFlowType.TYPE)
        };

        if (columnDefinition.inlineDatatype()) {
            const fieldType = DataFlowSchemaAdapterUtils._getFieldDataType(columnDefinition.inlineDatatype().datatype());
            fieldInformation.fieldType = fieldType || fieldInformation.fieldType;

            if (columnDefinition.inlineDatatype() && columnDefinition.inlineDatatype().dataFormat()) {
                let format = columnDefinition.inlineDatatype().dataFormat().getText() as string;
                if (format.startsWith('[')) {
                    format = DataFlowSchemaAdapterUtils.removeWrappingCharacters(format);
                    format = format.indexOf(',') > -1 ? format.split(',')[0] : format;
                }
                fieldInformation.fieldType.format = DataFlowSchemaAdapterUtils.removeWrappingCharacters(format);
            }
        }
        return fieldInformation;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _getFieldDataType(dataType: any): IDataFlowDataType {
        let fieldType: IDataFlowDataType;

        if (dataType.simple) {
            if (dataType.simple().DECIMAL) {
                fieldType = DataFlowTypesUtils.createType(DecimalDataFlowType.TYPE);
                (fieldType as DecimalDataFlowType).precision = parseInt(dataType.simple().precision.getText(), 10);
                (fieldType as DecimalDataFlowType).scale = parseInt(dataType.simple().scale.getText(), 10);
            } else {
                fieldType = DataFlowTypesUtils.createType(dataType.simple().getText());
            }
        } else if (dataType.complex) {
            const complexDataFlowType = new ComplexDataFlowType();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            dataType.complex().columnDefinition().forEach((nestedColumnDefinition: any) => {
                const nestedField = DataFlowSchemaAdapterUtils.getFieldInformation(nestedColumnDefinition);
                const dataFlowExpression = new DataFlowExpression();
                dataFlowExpression.expression = '';
                dataFlowExpression.returnType = nestedField.fieldType;
                complexDataFlowType.fields.push(new ComplexField(nestedField.fieldName, dataFlowExpression, complexDataFlowType));
            });
            fieldType = complexDataFlowType;
        } else if (dataType.OPEN_CLOSE_SQ) {
            const arrayField = new ArrayDataFlowType();
            arrayField.arrayType = DataFlowSchemaAdapterUtils._getFieldDataType(dataType.datatype());
            fieldType = arrayField;
        } else if (dataType.mapKey && dataType.mapValue) {
            const keyType = DataFlowSchemaAdapterUtils._getFieldDataType(dataType.mapKey);
            const valueType = DataFlowSchemaAdapterUtils._getFieldDataType(dataType.mapValue);
            fieldType = new MapDataFlowType(keyType, valueType);
        }

        return fieldType;
    }
}
